// src/components/JobView/InvoiceModal.js
// Subir la FACTURA del trabajo (solo Admin / rol Administración). Al guardar, marca el
// job como Finalizado. Visible/accesible solo donde se monte con el permiso adecuado.
import React, { useState } from "react";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { X, FileText, CheckCircle2 } from "lucide-react";

const InvoiceModal = ({ isOpen, onClose, db, storage, user, jobData, onPrepareTicket }) => {
  const [number, setNumber] = useState(jobData?.invoice?.number || "");
  const [amount, setAmount] = useState(jobData?.invoice?.amount || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;
  const existing = jobData?.invoice;

  const handleSave = async () => {
    if (!number.trim()) { setError("Ingresa el número de factura."); return; }
    setBusy(true); setError("");
    try {
      let url = existing?.url || "";
      let fileName = existing?.fileName || "";
      if (file) {
        const path = `jobs/${jobData.id}/invoice/${Date.now()}_${file.name}`;
        const r = ref(storage, path);
        await new Promise((res, rej) => { const t = uploadBytesResumable(r, file); t.on("state_changed", null, rej, res); });
        url = await getDownloadURL(r);
        fileName = file.name;
      }
      // Genera + sube el Job Ticket para adjuntarlo al correo de finalización.
      let jobTicketUrl = jobData?.jobTicketUrl || "";
      if (onPrepareTicket) { try { jobTicketUrl = await onPrepareTicket(); } catch (e) { console.error("No se pudo generar el Job Ticket:", e); } }
      await updateDoc(doc(db, "jobs", jobData.id), {
        invoice: { number: number.trim(), amount: Number(amount) || 0, url, fileName, uploadedBy: user?.email || null, uploadedAt: serverTimestamp() },
        ...(jobTicketUrl ? { jobTicketUrl } : {}),
        "status.phase": "Finalizado",
        "status.stage": "FINALIZADO",
      });
      onClose();
    } catch (e) { setError("No se pudo guardar la factura: " + (e?.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black bg-opacity-60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold flex items-center gap-2"><FileText size={20} /> Facturación — {jobData?.id}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </header>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">Sube la factura del trabajo. Al guardar, el job pasa a <b>Finalizado</b>.</p>
          {error && <div className="p-2 rounded bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>}
          {existing?.url && (
            <div className="text-sm text-green-700 flex items-center gap-1"><CheckCircle2 size={16} /> Factura actual: <a href={existing.url} target="_blank" rel="noreferrer" className="underline">{existing.fileName || existing.number}</a></div>
          )}
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-1">Número de factura</label>
            <input value={number} onChange={(e) => setNumber(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm" placeholder="Ej. F-001234" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-1">Monto (USD)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md text-sm" placeholder="0.00" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-1">Archivo (PDF)</label>
            <input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm" />
          </div>
        </div>
        <footer className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm">Cancelar</button>
          <button onClick={handleSave} disabled={busy} className="px-5 py-2 rounded-lg bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 disabled:opacity-50">{busy ? "Guardando…" : "Guardar y Finalizar"}</button>
        </footer>
      </div>
    </div>
  );
};

export default InvoiceModal;
